/**
 * Resolve a batch of Playwright `aria-ref` ids to DOM elements and run a
 * user-supplied browser-side function against each in bounded batched
 * `page.evaluate` calls.
 *
 * Why this helper exists
 * ----------------------
 * `page.ariaSnapshot({ mode: 'ai' })` mints ref ids and stores them on
 * `element._ariaRef` — a property in Playwright's **isolated world**.
 * The main-world DOM never sees an `aria-ref` HTML attribute, so any
 * code path that does `document.querySelector('[aria-ref="…"]')` from
 * inside `page.evaluate` is a silent no-op.
 *
 * The selector engine `aria-ref=…` *does* see those ids — it's the only
 * supported way to map a ref back to a DOM element from outside
 * Playwright's isolated world. This helper wraps that pattern:
 *
 *   1. Split refs into bounded chunks so a large snapshot cannot enqueue
 *      thousands of simultaneous CDP requests on the host event loop.
 *   2. Resolve each chunk to `ElementHandle`s via the selector engine
 *      (`scope.$('aria-ref=eN')`), pipelined within that chunk.
 *   3. Hand the chunk's handles + matching ref strings to
 *      `scope.evaluate(...)`. Inside that call, the user's `fn` runs once
 *      per element with `(el, ref)` — no DOM mutation, no fragile attribute
 *      baking. Handles are disposed before the next chunk starts.
 *
 * Use this everywhere a snapshot ref needs DOM-side enrichment
 * (label/value/editability/etc.). Replaces the legacy
 * `document.querySelector('[aria-ref="${ref}"]')` pattern.
 *
 * Sub-frames
 * ----------
 * Pass the matching `Frame` as `scope` to resolve refs that live inside
 * an iframe. Refs that don't resolve in the given scope return `null`
 * for that key.
 *
 * @module
 */

import type { ElementHandle, Frame, Page } from 'playwright';

/** Maximum simultaneous ref lookups/elements retained by the default path. */
export const DEFAULT_REF_RESOLUTION_BATCH_SIZE = 128;

export interface ResolveRefsOptions {
  /** Test/diagnostic seam. Production callers use the bounded default. */
  batchSize?: number;
  /** Stops scheduling new chunks after the current Playwright call settles. */
  signal?: AbortSignal;
}

function batchSizeFrom(options?: ResolveRefsOptions): number {
  const batchSize = options?.batchSize ?? DEFAULT_REF_RESOLUTION_BATCH_SIZE;
  if (!Number.isSafeInteger(batchSize) || batchSize <= 0) {
    throw new RangeError('resolveRefs batchSize must be a positive integer');
  }
  return batchSize;
}

function throwIfAborted(signal?: AbortSignal): void {
  if (!signal?.aborted) return;
  if (signal.reason !== undefined) throw signal.reason;
  const error = new Error('Ref resolution aborted');
  error.name = 'AbortError';
  throw error;
}

async function disposeHandles(handles: Array<ElementHandle<Element> | null>): Promise<void> {
  await Promise.all(
    handles.map((handle) => (handle ? handle.dispose().catch(() => undefined) : Promise.resolve()))
  );
}

/**
 * The browser-side function the caller wants to run against each
 * resolved element. Must be serializable; runs inside `scope.evaluate`.
 *
 * `el` is the resolved DOM element. `ref` is the ref string the caller
 * passed in (useful when fn produces results keyed by ref).
 */
export type RefHandler<T> = (el: Element, ref: string) => T;

/**
 * Arg-aware variant: each ref carries its own `args` payload (for
 * dispatch — e.g. "which checks should I run for this ref?") and the
 * call carries one `globals` payload shared by every ref (constants,
 * shared regex sources, etc.). The page-side fn receives
 * `(el, ref, args, globals)` and can branch.
 *
 * Splitting per-ref args from globals matters at scale: a 500-ref
 * snapshot with a 700-byte constants block was previously serializing
 * those constants 500× over the CDP boundary.
 */
export type RefHandlerWithArgs<A, G, T> = (el: Element, ref: string, args: A, globals: G) => T;

/**
 * Resolve every ref against `scope` (a `Page` or a `Frame`). Returns a
 * `Record<ref, T | null>`. Refs that the selector engine cannot resolve
 * in the given scope map to `null` and are skipped by `fn`.
 *
 * The browser-side `fn` runs once per element. Large inputs use multiple
 * bounded `scope.evaluate` calls while preserving result order and shape.
 */
export async function resolveRefs<T>(
  scope: Page | Frame,
  refs: readonly string[],
  fn: RefHandler<T>,
  options?: ResolveRefsOptions
): Promise<Record<string, T | null>> {
  if (refs.length === 0) return {};
  const batchSize = batchSizeFrom(options);
  const fnSource = fn.toString();
  const results: Record<string, T | null> = {};

  for (let start = 0; start < refs.length; start += batchSize) {
    throwIfAborted(options?.signal);
    const batchRefs = refs.slice(start, start + batchSize);
    const handles = await Promise.all(
      batchRefs.map((ref) =>
        scope
          .$(`aria-ref=${ref}`)
          .then((handle) => handle as ElementHandle<Element> | null)
          .catch(() => null)
      )
    );

    try {
      throwIfAborted(options?.signal);
      const batchResults = (await scope.evaluate(
        ({ els, refs: rs, fnSource: src }) => {
          // eslint-disable-next-line no-new-func
          const fnInPage = new Function(`return (${src})`)() as RefHandler<unknown>;
          const out: Record<string, unknown> = {};
          for (let i = 0; i < rs.length; i++) {
            const el = els[i];
            const ref = rs[i];
            if (!el) {
              out[ref] = null;
              continue;
            }
            try {
              out[ref] = fnInPage(el, ref);
            } catch {
              out[ref] = null;
            }
          }
          return out;
        },
        { els: handles, refs: batchRefs, fnSource }
      )) as Record<string, T | null>;
      throwIfAborted(options?.signal);
      Object.assign(results, batchResults);
    } finally {
      await disposeHandles(handles);
    }
  }

  throwIfAborted(options?.signal);
  return results;
}

/**
 * Diagnostics emitted alongside the per-ref result map. Lets callers
 * (e.g. the orchestrator) detect "every check returned null for every
 * ref" — the failure shape that originally hid the broken
 * `[aria-ref="..."]` CSS selector for years.
 */
export interface ResolveRefsDiagnostics {
  /** How many `(ref, args)` items were sent. */
  requested: number;
  /** How many resolved to a real DOM element. */
  resolved: number;
  /** How many produced a non-null result from the page-side fn. */
  succeeded: number;
}

/**
 * Like `resolveRefs`, but each `(ref, args)` pair lets the caller
 * dispatch per-ref work inside the browser-side fn, plus one shared
 * `globals` payload that's serialized once for the whole batch.
 *
 * Returns the result map alongside diagnostics so callers can
 * detect mass-null patterns without parsing the result themselves.
 */
export async function resolveRefsWithArgs<A, G, T>(
  scope: Page | Frame,
  items: ReadonlyArray<{ ref: string; args: A }>,
  globals: G,
  fn: RefHandlerWithArgs<A, G, T>,
  options?: ResolveRefsOptions
): Promise<{ results: Record<string, T | null>; diagnostics: ResolveRefsDiagnostics }> {
  const diagnostics: ResolveRefsDiagnostics = {
    requested: items.length,
    resolved: 0,
    succeeded: 0,
  };
  if (items.length === 0) return { results: {}, diagnostics };
  const batchSize = batchSizeFrom(options);
  const fnSource = fn.toString();
  const results: Record<string, T | null> = {};

  for (let start = 0; start < items.length; start += batchSize) {
    throwIfAborted(options?.signal);
    const batchItems = items.slice(start, start + batchSize);
    const handles = await Promise.all(
      batchItems.map((item) =>
        scope
          .$(`aria-ref=${item.ref}`)
          .then((handle) => handle as ElementHandle<Element> | null)
          .catch(() => null)
      )
    );
    diagnostics.resolved += handles.reduce((count, handle) => count + (handle ? 1 : 0), 0);

    try {
      throwIfAborted(options?.signal);
      const batchResults = (await scope.evaluate(
        ({ els, items: pairs, globals: g, fnSource: src }) => {
          // eslint-disable-next-line no-new-func
          const fnInPage = new Function(`return (${src})`)() as RefHandlerWithArgs<
            unknown,
            unknown,
            unknown
          >;
          const out: Record<string, unknown> = {};
          for (let i = 0; i < pairs.length; i++) {
            const el = els[i];
            const { ref, args } = pairs[i];
            if (!el) {
              out[ref] = null;
              continue;
            }
            try {
              out[ref] = fnInPage(el, ref, args, g);
            } catch {
              out[ref] = null;
            }
          }
          return out;
        },
        {
          els: handles,
          items: batchItems.map((item) => ({ ref: item.ref, args: item.args })),
          globals,
          fnSource,
        }
      )) as Record<string, T | null>;
      throwIfAborted(options?.signal);
      Object.assign(results, batchResults);
      diagnostics.succeeded += Object.values(batchResults).reduce(
        (count, value) => count + (value === null || value === undefined ? 0 : 1),
        0
      );
    } finally {
      await disposeHandles(handles);
    }
  }

  throwIfAborted(options?.signal);
  return { results, diagnostics };
}
